import React, { useCallback, useMemo, useState } from "react";
import {
  AppBar,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Toolbar,
  Typography,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { StyledDialog } from "customStyled";

import { useSelector } from "react-redux";
import { DynamicAvatar } from "Reuse/UI";
import { getUserStoreData, selectDistrictData } from "store/slices";
import ChangeCircleOutlinedIcon from "@mui/icons-material/ChangeCircleOutlined";
import Dropdown from "components/layout/Dropdown";
import { isValidValue, logger, sortArray } from "utils";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

const Header = React.memo(() => {
  const { t } = useTranslation("layout");
  // Get district data from Redux store
  const { name, logo_url, id: districtId } = useSelector(selectDistrictData);
  const [open, setOpen] = useState(false);

  const clickOpen = useCallback(
    (e) => {
      e?.preventDefault();
      setOpen(true);
    },
    [setOpen],
  );

  const onClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const switchDistrictIcon = useMemo(
    () => <SwitchDistrictIcon clickOpen={clickOpen} />,
    [clickOpen],
  );

  return (
    // AppBar: fixed top header bar
    <AppBar
      elevation={0}
      component="header"
      sx={{ background: "#101828", zIndex: 9999 }}
    >
      <Toolbar sx={{ justifyContent: "space-between", minHeight: 56 }}>
        {/* Center section: District title + logo/avatar */}
        <Stack sx={{ flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 1.5, flexGrow: 1 }}
        >
          <Typography>{t("district")}</Typography>

          {/* If district data available → show avatar, else skeleton loader */}
          {!!districtId ? (
            <DynamicAvatar
              sizes="small"
              alt={name}
              src={logo_url}
              sx={{ border: "1px solid #DBDDE0" }}
            />
          ) : (
            <Skeleton
              variant="circular"
              sx={{ bgcolor: "#ffffff" }}
              width={36}
              height={36}
            />
          )}

          {/* District name (or skeleton while loading) */}
          <Typography
            variant="body1"
            sx={(theme) => ({
              fontWeight: 500,
              lineHeight: "16px",
              ...theme.mixins.overflow, // handles text overflow (ellipsis)
            })}
          >
            {name ?? (
              <Skeleton sx={{ bgcolor: "#ffffff" }} width={100} height={22} />
            )}
          </Typography>
          {switchDistrictIcon}
        </Stack>
      </Toolbar>
      <DistrictListModel open={open} onClose={onClose} />

      {/* Divider below header */}
      <Divider />
    </AppBar>
  );
});

export default Header;

const DistrictListModel = (props) => {
  const { t } = useTranslation("layout");
  const { open, onClose } = props;
  const navigate = useNavigate();
  const { districts, id } = useSelector(getUserStoreData);
  const district = useSelector(selectDistrictData);

  const toogleEvent = useCallback(
    async (e, data) => {
      e?.preventDefault();
      try {
        navigate("/home", { state: { id: data.id, userId: id } });
        onClose();
      } catch (error) {
        logger.error("change district:-", error);
      }
    },
    [navigate, id, onClose],
  );

  return (
    <StyledDialog
      onClose={onClose}
      aria-labelledby="district-dialog-title"
      open={open}
      sx={(theme) => ({
        "& .MuiDialogContent-root": {
          padding: theme.spacing(0),
        },
      })}
    >
      <DialogTitle sx={{ m: 0, p: 2 }} id="district-dialog-title">
        {t("districts")}
      </DialogTitle>
      <DialogContent sx={{ width: 400 }} dividers>
        <Dropdown
          data={sortArray(
            districts.map((el) => ({
              ...el,
              disabled: isValidValue(el.deletedAt),
              disabledText:
                t("thisDistrictIsDeactivatedPlease"),
            })),
            "name",
          )}
          selected={[{ ...district }]}
          toogleEvent={toogleEvent}
        />
      </DialogContent>
    </StyledDialog>
  );
};

const SwitchDistrictIcon = ({ clickOpen }) => {
  const { t } = useTranslation("layout");
  const { districts } = useSelector(getUserStoreData);
  return (
    <React.Fragment>
      {Array.isArray(districts) && districts.length > 1 ? (
        <IconButton onClick={clickOpen} aria-label={t("switchDistrict")}>
          <ChangeCircleOutlinedIcon sx={{ color: "#ffffff" }} />
        </IconButton>
      ) : null}
    </React.Fragment>
  );
};
